import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import Button from "components/buttons/Button";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { Modal } from "components/Modal/Modal";
import {
  handleUpdateDraftItem,
  handleClearDraftItem,
  createRiskKycRulesItem,
  updateRiskKycRulesItem,
  getRiskKycRulesItem,
  type RiskKycRulesItem,
  type KycRulesType
} from "store/risk/kyc/rules/slice";

import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import getSymbolFromCurrency from 'currency-symbol-map';
import SwitchBtn from "components/buttons/SwitchBtn";
import styles from './RiskKycRulesEditModal.module.scss';
import cn from 'clsx';
import { usePermissionHelper } from "helpers/usePermissionHelper";

import { formatKycRuleType } from "helpers/kycRuleTypeLabels";

const RiskKycRulesEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const formErrors = useFormErrors();
  const permissions = usePermissionHelper();

  const isItemFetched = useAppSelector(
    (store) => store.riskKycRules.isItemFetched,
  );
  
  const isItemFetching = useAppSelector(
    (store) => store.riskKycRules.isItemFetching,
  );

  const draftItem = useAppSelector((store) => store.riskKycRules.draftItem);

  const entityId = searchParams.get("entityId") ?? "";
  const canModify = entityId ? permissions?.update : permissions?.create;
  
  const currenciesToRender
    = useAppSelector(state => state.settings.currencies).map(v => ({
      value: v,
      label: `${getSymbolFromCurrency(v)} ${v}`
    }));
  
  const typesToRender = useAppSelector(state => state.riskKycRules.type).map(v => ({
      value: v,
      label: formatKycRuleType(v)
    }));

  const handleClose = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearDraftItem());
  };

  const handleSave = async () => {
    if (!canModify) return;

    if (!draftItem.title || !draftItem.amount || !draftItem.type || !draftItem.currency) {

      if (!draftItem.title) formErrors.setFieldError('title', t('NotEmpty'));
      if (!draftItem.amount) formErrors.setFieldError('amount', t('amountErrorBigger0'));
      if (!draftItem.code) formErrors.setFieldError('type', t('selectOptionError'));
      if (!draftItem.code) formErrors.setFieldError('currency', t('selectOptionError'));
      
      return;
    }

    if (entityId) {
      dispatch(updateRiskKycRulesItem(draftItem as RiskKycRulesItem))
        .unwrap()
        .then(() => {
          dispatch(
            showNotification({
              title: t("success"),
              message: t("entityUpdateSuccess"),
              type: NotificationTypes.success,
            }),
          );
        })
        .catch((error) => {
          handleApiError({ error, t, dispatch, formErrors });
        });
      
      return;
    }

    dispatch(createRiskKycRulesItem(draftItem as RiskKycRulesItem))
      .unwrap()
      .then(() => {
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        handleClose();
      })
      .catch((error) => {
        handleApiError({ error, t, dispatch, formErrors });
      });
  };

  const handleUpdateField = <
    K extends keyof RiskKycRulesItem,
  >(
    key: K,
    value: RiskKycRulesItem[K],
  ) => {
    const updatedField = { [key]: value };

    switch (key) {
      case "type":
        dispatch(handleUpdateDraftItem({
          ...updatedField,
          code: `${value}_${draftItem.amount ?? ''}_${draftItem.currency ?? ''}`.toLowerCase(),
        }));
        return;
        case "amount":
        dispatch(handleUpdateDraftItem({
          ...updatedField,
          code: `${draftItem.type ?? ''}_${value}_${draftItem.currency ?? ''}`.toLowerCase(),
        }));
        return;
        case "currency":
        dispatch(handleUpdateDraftItem({
          ...updatedField,
          code: `${draftItem.type ?? ''}_${draftItem.amount ?? ''}_${value}`.toLowerCase(),
        }));
        return;
      default:
         dispatch(handleUpdateDraftItem(updatedField))
    };
  };

  const isLoaded = () => {
    if (entityId !== "" && !isItemFetched) {
      return false;
    }

    return true;
  };

  const canBeApplied = () => {
    if (formErrors.hasError('title')
      || formErrors.hasError('type')
      || formErrors.hasError('currency')
      || formErrors.hasError('amount')
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (entityId !== "" && !isItemFetched) {
      dispatch(getRiskKycRulesItem(+entityId));
    }
  }, [searchParams, isItemFetched]);

  return (
    <>
      <Modal onClose={handleClose} size="sm">
        <Modal.Header>{t(`${entityId ? 'kycRulesEdit' : 'kycRulesCreate'}`)}</Modal.Header>
        {!isLoaded() ? <ModalSkeleton /> : (
          <>
            <Modal.Content>
              <div className="promo-content">
                <div className="pages-form-content mt-14">
                  <div className="bonus-form-content">
                    <div className="bonus-form-box">
                      <InnerInput
                        value={draftItem.title ?? ""}
                        placeholder={t("enterTitle")}
                        label={t("title")}
                        type="text"
                        id="kyc-rule-title"
                        tooltipText={t("title")}
                        tooltipPosition="top-left"
                        onChange={(e) => {
                          handleUpdateField("title", e);
                          formErrors.clearFieldError("title");
                        }}
                        hasError={formErrors.hasError('title')}
                        disabled={!canModify}
                      />

                      <InnerInput
                        maxCodePoints={2500}
                        value={draftItem.description ?? ""}
                        placeholder={t("enterText")}
                        label={t("description")}
                        type="textarea"
                        id="kyc-rule-description"
                        onChange={(e) => {
                          handleUpdateField("description", e);
                        }}
                        disabled={!canModify}
                      />

                      <InnerInput
                        value={draftItem.code ?? ""}
                        placeholder={t("enterCode")}
                        label={t("code")}
                        type="text"
                        id="kyc-rule-code"
                        tooltipText={t("code")}
                        tooltipPosition="top-left"
                        onChange={() => {}}
                        disabled
                      />


                    </div>


                    <div className={cn("bonus-form-box", styles.column)}>
                      <DefaultDropdown
                        value={draftItem.type}
                        placeholder={t("chooseType")}
                        label={t("Type")}
                        options={typesToRender}
                        isShort
                        onSelect={value => {
                          handleUpdateField("type", value as KycRulesType);
                          formErrors.clearFieldError("type");
                        }}
                        hasError={formErrors.hasError('type')}
                        disabled={!canModify}
                      />
                      <DefaultDropdown
                        value={draftItem.currency}
                        placeholder={t("chooseCurrency")}
                        label={t("Currency")}
                        options={currenciesToRender}
                        isShort
                        onSelect={(value) => {
                          handleUpdateField("currency", value as string);
                          formErrors.clearFieldError("currency");
                        }}
                        hasError={formErrors.hasError("currency")}
                        disabled={!canModify}
                      />

                      <InnerInput
                        value={draftItem.amount ?? `0`}
                        placeholder={t("enterAmount")}
                        label={t("amount")}
                        type="number"
                        inputMode="numeric"
                        id="kyc-rule-title"
                        tooltipText={t("amount")}
                        tooltipPosition="top-left"
                        onChange={value => {
                          handleUpdateField("amount", value);
                          formErrors.clearFieldError("amount");
                        }}
                        hasError={formErrors.hasError("amount")}
                        min={0}
                        disabled={!canModify}
                      />


                      <SwitchBtn
                        activeTxt={t("active")}
                        notActiveTxt={t("hidden")}
                        isActive={!!draftItem?.isActive}
                        handleClick={() => {
                          handleUpdateField(
                            "isActive",
                            draftItem?.isActive ? !draftItem?.isActive : true,
                          );
                        }}
                        disabled={!canModify}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Modal.Content>
            {canModify && (
              <Modal.Footer>
                <Button
                  isSecondary txt={t("cancel")}
                  handleClick={handleClose}
                />
                <Button
                  isPrimary
                  handleClick={handleSave}
                  txt={t("save")}
                  isLoad={isItemFetching}
                  disabled={canBeApplied()}
                />
              </Modal.Footer>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default RiskKycRulesEditModal;
